import type { StackFrame, Event, Exception } from '@sentry/types';

// Rewrites stacktrace data from `/assets-brotli` to just `/assets/`. Sentry will attempt to
// download the assets to fill in the context information, but it doesn't support brotli compressed
// assets. However due to our CloudFormation setup, if you request something from `/assets-brotli/`
// you'll be served the Brotli asset regardless of what encoding you actually asked for. This is a
// workaround to restore context information / sourcemaps for brotli compressed assets.
export const stackFrameAssetRewrite = (data: Event) => {
	// There isn't a strong type definition for the `data` object with
	// raven-js (it's literally `any`), so we have to do some shape validation to find the deep
	// part we're interested in (if it exists). We're also extra defensive by wrapping the whole
	// thing in a try/catch - if an error occurs we'll just return the data as-is.
	try {
		const rewriteStacktrace = (value: Exception) => {
			if (
				!(value && value.stacktrace && value.stacktrace.frames && value.stacktrace.frames.length)
			) {
				return;
			}

			value.stacktrace.frames.forEach((frame: StackFrame) => {
				if (!frame.filename) {
					return;
				}

				// Yes, we're manipulating the input, this is an implicit choice for this part
				// of the codebase
				// eslint-disable-next-line no-param-reassign
				frame.filename = frame.filename.replace('/assets-brotli/', '/assets/');
			});
		};

		// for non-error errors there can be a top-level `data.stacktrace`
		rewriteStacktrace(data);

		// for normal errors there can be exception.values, these might contain stacktraces
		if (!(data.exception && data.exception.values && data.exception.values.length)) {
			return data;
		}

		data.exception.values.forEach(rewriteStacktrace);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (ex: any) {
		// eslint-disable-next-line no-console
		console.warn('Unable to rewrite stacktrace filenames', ex);
	}
	return data;
};
